import React, { useState, useEffect, useCallback } from 'react';
import { fmt, api } from '../utils/api';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function IncomeTracker({ onViewIncome, refreshKey }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await api.getIncomeTracker();
      setData(d);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!data) return null;

  const maxMonthTotal = Math.max(...data.months.map((m) => Math.max(m.total, m.projected || 0)), 1);

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <h2 style={styles.title}>💰 Income Tracker</h2>
        <div style={styles.headerRight}>
          <span style={styles.ytdLabel}>YTD</span>
          <span style={styles.ytdValue}>{fmt.currency(data.ytdTotal)}</span>
        </div>
      </div>

      {onViewIncome && (
        <button style={styles.viewLink} onClick={onViewIncome}>
          View all income transactions →
        </button>
      )}

      <div style={styles.legend}>
        <span style={styles.legendItem}><span style={{ ...styles.legendDot, background: '#00d4aa' }} /> Bank-detected</span>
        <span style={styles.legendItem}><span style={{ ...styles.legendDot, background: '#a78bfa' }} /> Manually logged</span>
        <span style={styles.legendItem}><span style={{ ...styles.legendDot, background: '#fbbf24', border: '1px dashed #fbbf24' }} /> Estimated</span>
        <span style={styles.legendItem}><span style={{ ...styles.legendDot, background: '#374151', border: '1px dashed #4b5563' }} /> Projected</span>
        {data.estimateSource === 'stated-reconciled' && (
          <span style={styles.estimateSourceNote}>(reconciled against your stated annual income)</span>
        )}
      </div>

      <div style={styles.timelineScroll}>
        <div style={styles.timeline}>
          <div style={styles.rail} />
          {data.months.map((m, i) => (
            <MonthCapsule key={m.month} month={m} label={MONTH_LABELS[i]} maxTotal={maxMonthTotal} />
          ))}
        </div>
      </div>

      <div style={styles.cheatSheet}>
        <div style={styles.cheatRow}>
          <span style={styles.cheatLabel}>Earned Income (projected)</span>
          <span style={styles.cheatValueBig}>{fmt.currency(data.projectedAnnualIncome)}</span>
        </div>

        {data.investmentDrawIncluded && data.investmentDrawAnnual > 0 && (
          <>
            <div style={styles.cheatRow}>
              <span style={styles.cheatLabel}>+ Investment Draw</span>
              <span style={{ ...styles.cheatValueBig, color: '#38bdf8' }}>{fmt.currency(data.investmentDrawAnnual)}</span>
            </div>
            <div style={styles.divider} />
            <div style={styles.cheatRow}>
              <span style={styles.cheatLabel}>Total Money Coming In</span>
              <span style={{ ...styles.cheatValueBig, color: '#00d4aa' }}>{fmt.currency(data.totalAnnualMoneyIn)}</span>
            </div>
            <p style={styles.disclaimer}>
              Investment draw is set in Manage → Investment Goal. Its tax
              treatment varies (capital gains, return of principal, ordinary
              income for a retirement account, etc.), so it's kept separate
              from the earned-income tax estimate below.
            </p>
          </>
        )}

        {!data.taxState ? (
          <p style={styles.taxPrompt}>
            Set your tax state in Manage → Household Income to see estimated
            taxes and take-home pay here.
          </p>
        ) : data.taxEstimate && (
          <>
            <div style={styles.divider} />
            <div style={styles.taxGrid}>
              <TaxLine label="Federal tax (est.)" value={data.taxEstimate.federalTax} />
              <TaxLine label="State tax (est.)" value={data.taxEstimate.stateTax} />
              <TaxLine label="FICA (SS + Medicare)" value={data.taxEstimate.ficaTax} />
              <TaxLine label="Effective rate" value={`${data.taxEstimate.effectiveRate}%`} isPct />
            </div>
            <div style={styles.divider} />
            <div style={styles.cheatRow}>
              <span style={styles.cheatLabel}>Estimated Take-Home (earned income only)</span>
              <span style={styles.takeHome}>
                {fmt.currency(data.taxEstimate.netIncome)}/yr
                <span style={styles.takeHomeMonthly}> · {fmt.currency(data.taxEstimate.monthlyNet)}/mo</span>
              </span>
            </div>
            <p style={styles.disclaimer}>
              Rough estimate based on your projected income and standard deductions - not tax advice.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

const formatCompact = (v) => {
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return fmt.currency(v);
};

function MonthCapsule({ month, label, maxTotal }) {
  const bankHeight = Math.max((month.bank / maxTotal) * 100, month.bank > 0 ? 4 : 0);
  const manualHeight = Math.max((month.manual / maxTotal) * 100, month.manual > 0 ? 4 : 0);
  const estimatedHeight = Math.max(((month.estimated || 0) / maxTotal) * 100, month.estimated > 0 ? 4 : 0);
  const projectedHeight = Math.max(((month.projected || 0) / maxTotal) * 100, month.projected > 0 ? 4 : 0);
  const totalHeightPct = Math.max((month.total / maxTotal) * 100, month.total > 0 ? 4 : 0);

  return (
    <div style={styles.capsule}>
      <div style={styles.barColumn}>
        {month.isFuture ? (
          month.projected > 0 ? (
            <div style={styles.barInner}>
              <span style={{ ...styles.barValueLabel, bottom: `calc(${projectedHeight}% + 4px)`, color: '#4b5563' }}>
                {formatCompact(month.projected)}
              </span>
              <div style={styles.stackedBar}>
                <div
                  style={{ ...styles.barSegment, height: `${projectedHeight}%`, background: '#37415155', border: '1px dashed #4b5563' }}
                  title={`Projected: ${fmt.currency(month.projected)} - hasn't happened yet`}
                />
              </div>
            </div>
          ) : (
            <div style={styles.futureBar} />
          )
        ) : (
          <div style={styles.barInner}>
            {month.total > 0 && (
              <span style={{ ...styles.barValueLabel, bottom: `calc(${totalHeightPct}% + 4px)`, color: (month.isEstimated || month.isPartiallyEstimated) ? '#fbbf24' : undefined }}>
                {(month.isEstimated || month.isPartiallyEstimated) ? '≈' : ''}{formatCompact(month.total)}
              </span>
            )}
            <div style={styles.stackedBar}>
              {month.isEstimated ? (
                <div
                  style={{ ...styles.barSegment, height: `${estimatedHeight}%`, background: '#fbbf2455', border: '1px dashed #fbbf24' }}
                  title={`Estimated: ${fmt.currency(month.estimated)} - no transaction history for this month`}
                />
              ) : (
                <>
                  {month.manual > 0 && (
                    <div style={{ ...styles.barSegment, height: `${manualHeight}%`, background: '#a78bfa' }} title={`Manual: ${fmt.currency(month.manual)}`} />
                  )}
                  {month.bank > 0 && (
                    <div style={{ ...styles.barSegment, height: `${bankHeight}%`, background: '#00d4aa', boxShadow: '0 0 6px #00d4aa80' }} title={`Bank: ${fmt.currency(month.bank)}`} />
                  )}
                  {month.isPartiallyEstimated && month.estimated > 0 && (
                    <div
                      style={{ ...styles.barSegment, height: `${estimatedHeight}%`, background: '#fbbf2455', border: '1px dashed #fbbf24' }}
                      title={`+ ${fmt.currency(month.estimated)} estimated - transaction history for this month only starts partway through it`}
                    />
                  )}
                </>
              )}
              {month.total === 0 && <div style={styles.emptyBar} />}
            </div>
          </div>
        )}
      </div>
      <div style={{ ...styles.dot, ...(month.isCurrent ? styles.dotCurrent : {}) }} />
      <span style={{ ...styles.monthLabel, ...(month.isCurrent ? styles.monthLabelCurrent : {}) }}>{label}</span>
    </div>
  );
}

function TaxLine({ label, value, isPct }) {
  return (
    <div style={styles.taxLine}>
      <span style={styles.taxLineLabel}>{label}</span>
      <span style={styles.taxLineValue}>{isPct ? value : fmt.currency(value)}</span>
    </div>
  );
}

const styles = {
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
    marginBottom: '1rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  headerRight: { display: 'flex', alignItems: 'baseline', gap: '6px' },
  ytdLabel: {
    color: '#6b7280',
    fontSize: '0.7rem',
    fontFamily: "'Syne', sans-serif",
  },
  ytdValue: {
    color: '#00d4aa',
    fontSize: '1.1rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  legend: { display: 'flex', gap: '1rem', marginBottom: '1rem' },
  viewLink: {
    background: 'transparent',
    border: 'none',
    color: '#00d4aa',
    fontSize: '0.75rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    padding: 0,
    marginBottom: '0.75rem',
    display: 'block',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    color: '#6b7280',
    fontSize: '0.7rem',
    fontFamily: "'Syne', sans-serif",
  },
  legendDot: { width: '8px', height: '8px', borderRadius: '50%' },
  estimateSourceNote: {
    color: '#4b5563',
    fontSize: '0.65rem',
    fontStyle: 'italic',
    fontFamily: "'Syne', sans-serif",
  },
  timelineScroll: {
    overflowX: 'auto',
    paddingBottom: '0.5rem',
    marginBottom: '1rem',
  },
  timeline: {
    display: 'flex',
    gap: '0.4rem',
    position: 'relative',
    minWidth: '700px',
    paddingTop: '4px',
  },
  rail: {
    position: 'absolute',
    top: '148px',
    left: '20px',
    right: '20px',
    height: '2px',
    background: 'linear-gradient(90deg, #1e2a3a, #374151, #1e2a3a)',
  },
  capsule: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    position: 'relative',
  },
  barColumn: {
    height: '124px',
    display: 'flex',
    alignItems: 'flex-end',
    width: '60%',
    paddingTop: '20px',
    boxSizing: 'content-box',
  },
  barInner: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  barValueLabel: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '0.62rem',
    fontWeight: '700',
    color: '#e5e7eb',
    fontFamily: "'DM Mono', monospace",
    whiteSpace: 'nowrap',
    textShadow: '0 1px 3px rgba(0,0,0,0.8)',
  },
  stackedBar: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column-reverse',
    justifyContent: 'flex-start',
    borderRadius: '4px 4px 0 0',
    overflow: 'hidden',
  },
  barSegment: {
    width: '100%',
    transition: 'height 0.6s ease',
  },
  emptyBar: {
    width: '100%',
    height: '2px',
    background: '#1e2a3a',
  },
  futureBar: {
    width: '100%',
    height: '2px',
    background: 'repeating-linear-gradient(90deg, #1e2a3a 0, #1e2a3a 3px, transparent 3px, transparent 6px)',
    alignSelf: 'flex-end',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#374151',
    border: '2px solid #111827',
    zIndex: 1,
  },
  dotCurrent: {
    background: '#00d4aa',
    boxShadow: '0 0 8px #00d4aa',
  },
  monthLabel: {
    fontSize: '0.68rem',
    color: '#6b7280',
    fontFamily: "'Syne', sans-serif",
    fontWeight: '600',
  },
  monthLabelCurrent: { color: '#00d4aa' },
  cheatSheet: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '12px',
    padding: '1.1rem',
  },
  cheatRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  cheatLabel: {
    color: '#9ca3af',
    fontSize: '0.8rem',
    fontFamily: "'Syne', sans-serif",
  },
  cheatValueBig: {
    color: '#fff',
    fontSize: '1.3rem',
    fontWeight: '800',
    fontFamily: "'DM Mono', monospace",
  },
  taxPrompt: {
    color: '#6b7280',
    fontSize: '0.78rem',
    lineHeight: '1.5',
    marginTop: '0.6rem',
    marginBottom: 0,
  },
  divider: { height: '1px', background: '#1e2a3a', margin: '0.85rem 0' },
  taxGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.6rem',
  },
  taxLine: { display: 'flex', flexDirection: 'column', gap: '2px' },
  taxLineLabel: {
    color: '#6b7280',
    fontSize: '0.68rem',
    fontFamily: "'Syne', sans-serif",
  },
  taxLineValue: {
    color: '#e5e7eb',
    fontSize: '0.85rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  takeHome: {
    color: '#00d4aa',
    fontSize: '1.1rem',
    fontWeight: '800',
    fontFamily: "'DM Mono', monospace",
  },
  takeHomeMonthly: {
    color: '#6b7280',
    fontSize: '0.75rem',
    fontWeight: '500',
  },
  disclaimer: {
    color: '#4b5563',
    fontSize: '0.68rem',
    marginTop: '0.6rem',
    marginBottom: 0,
    fontStyle: 'italic',
  },
};
